import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Form,
    Input,
    Button,
    Card,
    Space,
    Select,
    Switch,
    Typography,
    Divider,
    Row,
    Col,
    TimePicker,
} from 'antd';
import {
    SaveOutlined,
    ArrowLeftOutlined,
    CalendarOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAppDispatch, useAppSelector } from 'store';
import {
    createSchedule,
    updateSchedule,
    fetchSchedule,
    fetchTemplates,
    fetchSegments,
    selectSchedules,
    selectTemplates,
    selectSegments,
    selectPushLoading,
    selectCurrentSchedule,
    setCurrentSchedule,
} from 'store/slices/push.slice';
import {
    fetchApplications,
    selectApplications,
} from 'store/slices/applications.slice';
import { RecurringScheduleCreateRequest } from 'types/push_management.types';
import { notify } from 'utils/helpers';
import { ROUTES } from 'config/routes';
import '../../styles/Common.css';
import {getStoredAppId} from 'utils/pushCenterStorage'

const { Title, Text: AntText } = Typography;

const TIMEZONES = [
    { value: 'Europe/Moscow', label: 'Europe/Moscow (MSK)' },
    { value: 'Europe/London', label: 'Europe/London (GMT)' },
    { value: 'Europe/Berlin', label: 'Europe/Berlin (CET)' },
    { value: 'America/New_York', label: 'America/New_York (EST)' },
    { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PST)' },
    { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' },
    { value: 'Asia/Shanghai', label: 'Asia/Shanghai (CST)' },
    { value: 'UTC', label: 'UTC' },
];

const DAY_CODE_TO_NAME: Record<string, string> = {
    MON: 'monday', TUE: 'tuesday', WED: 'wednesday',
    THU: 'thursday', FRI: 'friday', SAT: 'saturday', SUN: 'sunday',
};

const WEEKDAYS = [
    { value: 'monday', label: 'Понедельник' },
    { value: 'tuesday', label: 'Вторник' },
    { value: 'wednesday', label: 'Среда' },
    { value: 'thursday', label: 'Четверг' },
    { value: 'friday', label: 'Пятница' },
    { value: 'saturday', label: 'Суббота' },
    { value: 'sunday', label: 'Воскресенье' },
];

const RecurringScheduleForm: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const dispatch = useAppDispatch();
    const [form] = Form.useForm();

    const isEditMode = Boolean(id);
    const applications = useAppSelector(selectApplications);
    const schedules = useAppSelector(selectSchedules);
    const templates = useAppSelector(selectTemplates);
    const segments = useAppSelector(selectSegments);
    const loading = useAppSelector(selectPushLoading);
    const currentSchedule = useAppSelector(selectCurrentSchedule);

    const [selectedApp, setSelectedApp] = useState<number | null>(null);
    const [scheduleType, setScheduleType] = useState<string>('daily');

    useEffect(() => {
        dispatch(fetchApplications({ limit: 100, offset: 0 }));

        if (isEditMode && id) {
            dispatch(fetchSchedule(parseInt(id)));
        } else {
            dispatch(setCurrentSchedule(null));
            const storedAppId = getStoredAppId();
            if (storedAppId) {
                setSelectedApp(storedAppId);
                form.setFieldValue('app_id', storedAppId);
            }
        }
    }, [dispatch, isEditMode, id]);

    useEffect(() => {
        if (!isEditMode || !id) return;

        const scheduleId = parseInt(id);
        const schedule =
            currentSchedule?.id === scheduleId
                ? currentSchedule
                : schedules.find(s => s.id === scheduleId);

        if (!schedule) return;

        setSelectedApp(schedule.app_id);
        setScheduleType(schedule.schedule_type);

        let scheduleValuePart = '';
        let scheduleTime = '';

        if (schedule.schedule_type === 'daily') {
            scheduleTime = schedule.schedule_value;
        } else if (schedule.schedule_type === 'weekly') {
            const spaceIdx = schedule.schedule_value.lastIndexOf(' ');
            const dayCode = schedule.schedule_value.slice(0, spaceIdx);
            scheduleTime = schedule.schedule_value.slice(spaceIdx + 1);
            scheduleValuePart = DAY_CODE_TO_NAME[dayCode] ?? dayCode.toLowerCase();
        } else if (schedule.schedule_type === 'monthly') {
            const [day, time] = schedule.schedule_value.split(' ');
            scheduleValuePart = day;
            scheduleTime = time ?? '';
        }

        form.setFieldsValue({
            app_id: schedule.app_id,
            name: schedule.name,
            schedule_type: schedule.schedule_type,
            schedule_value: scheduleValuePart || undefined,
            schedule_time: scheduleTime ? dayjs(scheduleTime, 'HH:mm') : undefined,
            push_template_id: schedule.push_template_id,
            segment_id: schedule.segment_id ?? undefined,
            timezone: schedule.timezone,
            is_active: schedule.is_active,
        });
    }, [isEditMode, id, currentSchedule, schedules, form]);

    useEffect(() => {
        if (selectedApp) {
            dispatch(fetchTemplates(selectedApp));
            dispatch(fetchSegments({ appId: selectedApp }));
        }
    }, [dispatch, selectedApp]);

    const handleSubmit = async (values: any) => {
        try {
            const scheduleTime = values.schedule_time ? values.schedule_time.format('HH:mm') : '';
            let scheduleValue = '';
            if (values.schedule_type === 'daily') {
                scheduleValue = scheduleTime;
            } else {
                scheduleValue = values.schedule_value;
            }

            const base = {
                name: values.name,
                push_template_id: values.push_template_id,
                segment_id: values.segment_id ?? null,
                schedule_type: values.schedule_type,
                schedule_value: scheduleValue,
                schedule_time: scheduleTime,
                timezone: values.timezone,
                is_active: values.is_active,
            };

            if (isEditMode && id) {
                await dispatch(updateSchedule({ id: parseInt(id), data: base })).unwrap();
                notify.success('Расписание успешно обновлено');
            } else {
                await dispatch(createSchedule({
                    ...base,
                    app_id: values.app_id,
                    start_date: new Date().toISOString(),
                } as RecurringScheduleCreateRequest)).unwrap();
                notify.success('Расписание успешно создано');
            }

            navigate(ROUTES.PUSH_CENTER);
        } catch (error: any) {
            notify.error(error.message || 'Ошибка при сохранении расписания');
        }
    };

    const renderScheduleValueField = () => {
        switch (scheduleType) {
            case 'daily':
                return null;

            case 'weekly':
                return (
                    <Form.Item
                        label="День недели"
                        name="schedule_value"
                        rules={[{ required: true, message: 'Выберите день недели' }]}
                    >
                        <Select placeholder="Выберите день" size="large">
                            {WEEKDAYS.map(day => (
                                <Select.Option key={day.value} value={day.value}>
                                    {day.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                );

            case 'monthly':
                return (
                    <Form.Item
                        label="День месяца"
                        name="schedule_value"
                        rules={[
                            { required: true, message: 'Укажите день месяца' },
                            {
                                validator: (_, value) => {
                                    const num = parseInt(value);
                                    if (num >= 1 && num <= 31) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject('День должен быть от 1 до 31');
                                },
                            },
                        ]}
                        extra="От 1 до 31"
                    >
                        <Input
                            placeholder="1"
                            size="large"
                            type="number"
                            min={1}
                            max={31}
                        />
                    </Form.Item>
                );

            default:
                return null;
        }
    };

    return (
        <div className="page-container">
            <div className="page-header">
                <Space>
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate(ROUTES.PUSH_CENTER)}
                    >
                        Назад
                    </Button>
                    <Title level={2}>
                        {isEditMode ? 'Редактирование расписания' : 'Создание расписания'}
                    </Title>
                </Space>
            </div>
            <Card>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{
                        is_active: true,
                        timezone: 'Europe/Moscow',
                        schedule_type: 'daily',
                    }}
                >
                    <div>
                        <AntText strong style={{ fontSize: 16 }}>Основные настройки</AntText>
                        <Divider />
                    </div>

                    <Form.Item
                        label="Приложение"
                        name="app_id"
                        rules={[{ required: true, message: 'Выберите приложение' }]}
                    >
                        <Select
                            placeholder="Выберите приложение"
                            size="large"
                            showSearch
                            optionFilterProp="children"
                            disabled={isEditMode}
                            onChange={setSelectedApp}
                        >
                            {applications.map(app => (
                                <Select.Option key={app.id} value={app.id}>
                                    {app.name} ({app.bundle_id})
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Название расписания"
                        name="name"
                        rules={[
                            { required: true, message: 'Введите название' },
                            { min: 3, message: 'Минимум 3 символа' },
                            { max: 100, message: 'Максимум 100 символов' },
                        ]}
                    >
                        <Input
                            placeholder="Например: Daily Tournament Reminder"
                            size="large"
                        />
                    </Form.Item>

                    <div style={{ marginTop: 24 }}>
                        <AntText strong style={{ fontSize: 16 }}>Расписание</AntText>
                        <Divider />
                    </div>

                    <Form.Item
                        label="Тип расписания"
                        name="schedule_type"
                        rules={[{ required: true, message: 'Выберите тип' }]}
                    >
                        <Select
                            placeholder="Выберите тип"
                            size="large"
                            onChange={setScheduleType}
                        >
                            <Select.Option value="daily">
                                <Space>
                                    <CalendarOutlined />
                                    Ежедневно
                                </Space>
                            </Select.Option>
                            <Select.Option value="weekly">
                                <Space>
                                    <CalendarOutlined />
                                    Еженедельно
                                </Space>
                            </Select.Option>
                            <Select.Option value="monthly">
                                <Space>
                                    <CalendarOutlined />
                                    Ежемесячно
                                </Space>
                            </Select.Option>
                        </Select>
                    </Form.Item>

                    <Row gutter={16}>
                        {renderScheduleValueField() && (
                            <Col xs={24} md={12}>
                                {renderScheduleValueField()}
                            </Col>
                        )}

                        <Col xs={24} md={scheduleType === 'daily' ? 24 : 12}>
                            <Form.Item
                                label="Время отправки"
                                name="schedule_time"
                                rules={[{ required: true, message: 'Укажите время' }]}
                            >
                                <TimePicker
                                    format="HH:mm"
                                    placeholder="12:00"
                                    size="large"
                                    style={{ width: '100%' }}
                                    minuteStep={15}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        label="Часовой пояс (Timezone)"
                        name="timezone"
                        rules={[{ required: true, message: 'Выберите timezone' }]}
                        extra="Время отправки будет в указанном часовом поясе"
                    >
                        <Select
                            placeholder="Выберите timezone"
                            size="large"
                            showSearch
                            optionFilterProp="children"
                        >
                            {TIMEZONES.map(tz => (
                                <Select.Option key={tz.value} value={tz.value}>
                                    {tz.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <div style={{ marginTop: 24 }}>
                        <AntText strong style={{ fontSize: 16 }}>Контент и аудитория</AntText>
                        <Divider />
                    </div>

                    <Form.Item
                        label="Шаблон push-уведомления"
                        name="push_template_id"
                        rules={[{ required: true, message: 'Выберите шаблон' }]}
                    >
                        <Select
                            placeholder="Выберите шаблон"
                            size="large"
                            disabled={!selectedApp}
                            showSearch
                            optionFilterProp="children"
                            optionLabelProp="label"
                        >
                            {templates.map(template => (
                                <Select.Option
                                    key={template.id}
                                    value={template.id}
                                    label={template.name}
                                >
                                    <div>
                                        <div style={{ fontWeight: 500 }}>{template.name}</div>
                                        <div style={{ fontSize: 12, color: '#666' }}>
                                            {template.title}
                                        </div>
                                    </div>
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Сегмент (опционально)"
                        name="segment_id"
                        extra="Если не выбран, расписание будет отправлять push всем пользователям"
                    >
                        <Select
                            placeholder="Все пользователи"
                            size="large"
                            disabled={!selectedApp}
                            allowClear
                            showSearch
                            optionFilterProp="children"
                        >
                            {segments.map(segment => (
                                <Select.Option key={segment.id} value={segment.id}>
                                    {segment.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Статус"
                        name="is_active"
                        valuePropName="checked"
                    >
                        <Switch
                            checkedChildren="Активно"
                            unCheckedChildren="Неактивно"
                        />
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 0, marginTop: 32 }}>
                        <Space>
                            <Button
                                type="primary"
                                htmlType="submit"
                                icon={<SaveOutlined />}
                                size="large"
                                loading={loading}
                            >
                                {isEditMode ? 'Сохранить изменения' : 'Создать расписание'}
                            </Button>
                            <Button
                                size="large"
                                onClick={() => navigate(ROUTES.PUSH_CENTER)}
                            >
                                Отмена
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
};

export default RecurringScheduleForm;